import { useEffect, useState } from 'react';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useTargetVisitsStore } from '../store/targetVisitsStore';
import {
  formatTargetScopeKey,
  type TargetScope,
} from '../utils/target-scope.utils';

type SessionBaselineKind = 'assets' | 'home';

const SESSION_BASELINE_SELECTORS = {
  assets: {
    baselines: (state: ReturnType<typeof useTargetVisitsStore.getState>) =>
      state.assetsSessionBaselines,
    consume: (state: ReturnType<typeof useTargetVisitsStore.getState>) =>
      state.consumeAssetsVisitBaseline,
  },
  home: {
    baselines: (state: ReturnType<typeof useTargetVisitsStore.getState>) =>
      state.homeSessionBaselines,
    consume: (state: ReturnType<typeof useTargetVisitsStore.getState>) =>
      state.consumeHomeVisitBaseline,
  },
} as const satisfies Record<SessionBaselineKind, unknown>;

export function useSessionVisitBaseline({
  kind,
  targetId,
  targetType,
}: {
  kind: SessionBaselineKind;
  targetId: string | null;
  targetType: TargetTypeType;
}): { baselineAt: Date | null; isReady: boolean } {
  const scopeKey = targetId
    ? formatTargetScopeKey({ targetId, targetType } satisfies TargetScope)
    : null;
  const selectors = SESSION_BASELINE_SELECTORS[kind];

  const [hasHydrated, setHasHydrated] = useState(() =>
    useTargetVisitsStore.persist.hasHydrated(),
  );
  const consumeBaseline = useTargetVisitsStore(selectors.consume);
  const sessionBaselineMs = useTargetVisitsStore((state) =>
    scopeKey ? selectors.baselines(state)[scopeKey] : undefined,
  );

  useEffect(() => {
    const unsubscribe = useTargetVisitsStore.persist.onFinishHydration(() => {
      setHasHydrated(true);
    });

    if (useTargetVisitsStore.persist.hasHydrated()) {
      setHasHydrated(true);
    }

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!hasHydrated || !scopeKey) {
      return;
    }

    consumeBaseline(scopeKey);
  }, [consumeBaseline, hasHydrated, scopeKey]);

  const isReady = hasHydrated && sessionBaselineMs !== undefined;

  return {
    baselineAt:
      isReady && sessionBaselineMs !== null
        ? new Date(sessionBaselineMs)
        : null,
    isReady,
  };
}
